"use client";

import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import * as React from "react";

import { cn } from "@/lib/utils";

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    scrollShadowOffset?: number;
  }
>(({ className, children, scrollShadowOffset = 20, ...props }, ref) => {
  const [showTopShadow, setShowTopShadow] = React.useState(false);
  const [showBottomShadow, setShowBottomShadow] = React.useState(false);
  const [showLeftShadow, setShowLeftShadow] = React.useState(false);
  const [showRightShadow, setShowRightShadow] = React.useState(false);
  const viewportRef = React.useRef<HTMLDivElement>(null);

  const checkScrollPosition = React.useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const {
      scrollTop,
      scrollHeight,
      clientHeight,
      scrollLeft,
      scrollWidth,
      clientWidth,
    } = viewport;
    setShowTopShadow(scrollTop > scrollShadowOffset / 2);
    setShowBottomShadow(
      scrollTop + clientHeight < scrollHeight - scrollShadowOffset / 2,
    );
    setShowLeftShadow(scrollLeft > scrollShadowOffset / 2);
    setShowRightShadow(
      scrollLeft + clientWidth < scrollWidth - scrollShadowOffset / 2,
    );
  }, [scrollShadowOffset]);

  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    // Check initial state
    checkScrollPosition();

    // Check on scroll
    viewport.addEventListener("scroll", checkScrollPosition);

    // Check on resize (content might change)
    const resizeObserver = new ResizeObserver(checkScrollPosition);
    resizeObserver.observe(viewport);

    return () => {
      viewport.removeEventListener("scroll", checkScrollPosition);
      resizeObserver.disconnect();
    };
  }, [checkScrollPosition]);

  const scrollShadowOffsetVar = {
    "--scroll-shadow-offset": `${scrollShadowOffset}px`,
  } as React.CSSProperties;

  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      {/* Top shadow */}
      <div
        className={cn(
          "absolute top-0 left-0 right-0 h-[var(--scroll-shadow-offset)] bg-gradient-to-b from-gray-900/10 to-transparent",
          "pointer-events-none z-10 transition-opacity duration-200",
          showTopShadow ? "opacity-80" : "opacity-0",
        )}
        style={scrollShadowOffsetVar}
      />

      {/* Left shadow */}
      <div
        className={cn(
          "absolute top-0 left-0 bottom-0 w-[var(--scroll-shadow-offset)] bg-gradient-to-r from-gray-900/10 to-transparent",
          "pointer-events-none z-10 transition-opacity duration-200",
          showLeftShadow ? "opacity-80" : "opacity-0",
        )}
        style={scrollShadowOffsetVar}
      />

      {/* Right shadow */}
      <div
        className={cn(
          "absolute top-0 right-0 bottom-0 w-[var(--scroll-shadow-offset)] bg-gradient-to-l from-gray-900/10 to-transparent",
          "pointer-events-none z-10 transition-opacity duration-200",
          showRightShadow ? "opacity-80" : "opacity-0",
        )}
        style={scrollShadowOffsetVar}
      />

      {/* Bottom shadow */}
      <div
        className={cn(
          "absolute bottom-0 left-0 right-0 h-[var(--scroll-shadow-offset)] bg-gradient-to-t from-gray-900/10 to-transparent",
          "pointer-events-none z-10 transition-opacity duration-200",
          showBottomShadow ? "opacity-80" : "opacity-0",
        )}
        style={scrollShadowOffsetVar}
      />

      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        className="h-full w-full rounded-[inherit]"
      >
        {children}
      </ScrollAreaPrimitive.Viewport>

      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
});
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" &&
        "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
